import {Injectable, NgZone, OnDestroy} from "@angular/core";
import {ClientContextService} from "../../client.context/services/client.context.service";
import {BehaviorSubject, Observable} from "rxjs";
import {TimezoneProxy} from "./timezone.proxy";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {first} from "rxjs/operators";
import {LogManager} from "../../log";

@UntilDestroy()
@Injectable()
export class TimezoneService implements OnDestroy {
	public serverOffsetFromUTCSubject: BehaviorSubject<number>;
	public serverClientOffsetDifferenceSubject: BehaviorSubject<number>;
	public serverTimeSubject: BehaviorSubject<Date>;
	public checkServerUtcMinutesOffsetTimer;
	public checkOffsetDifferenceStateTimer;

	constructor(private clientContextService: ClientContextService,
				private ngZone: NgZone,
				private timezoneProxy: TimezoneProxy) {
		this.serverOffsetFromUTCSubject = new BehaviorSubject<number>(null);
		this.serverClientOffsetDifferenceSubject = new BehaviorSubject<number>(null);
		this.serverTimeSubject = new BehaviorSubject<Date>(null);

		this.ngZone.runOutsideAngular(() => {
			this.checkServerUtcMinutesOffsetTimer = setInterval(() => this.checkServerUtcMinutesOffset(), 3600000);
			this.checkOffsetDifferenceStateTimer = setInterval(() => this.checkOffsetDifferenceState(), 5000);
		});
	}

	public ngOnDestroy(): void { }

	/**
	 * Calculates current server time based on client time and offset difference.
	 */
	public getServerTime(): Date {
		let d = new Date();
		d.setTime(d.getTime() - this.getStoredOffsetDifference()*60*1000);
		return d;
	}

	/**
	 * Updates offset difference in the log manager for proper logging time that shall be a server time.
	 */
	public setLogManagerOffsetDifference() {
		LogManager.offsetDifference = this.getStoredOffsetDifference();
	}

	/**
	 * The difference between client and server offsets. If not equals to 0, means that client and server are in different timezones.
	 * Used to calculate the current server time based on client time and offset difference.
	 */
	public getStoredOffsetDifference(): number {
		return this.serverClientOffsetDifferenceSubject.getValue();
	}

	/**
	 * Observable of client and server offset difference change.
	 */
	public getOffsetDifferenceObservable(): Observable<number> {
		return this.serverClientOffsetDifferenceSubject.asObservable();
	}

	/**
	 * Gets current client offset.
	 */
	public getLocalOffsetFromUTC():number {
		return -new Date().getTimezoneOffset();
	}

	/**
	 * Gets current server offset value stored on client.
	 */
	public getServerOffsetFromUTC(): number {
		return this.serverOffsetFromUTCSubject.getValue();
	}

	/**
	 * Observable of the server time. The server time subject is updated once in 5 seconds in order to be displayed somewhere on client if needed.
	 */
	public getServerTimeObservable(): Observable<Date> {
		return this.serverTimeSubject.asObservable();
	}

	/**
	 * Checks the server offset. In case the result differs from the stored value on client, updates the value on client.
	 * Runs on the system load and once in an hour according to the special task.
	 */
	public loadServerOffsetFromUTC(): Observable<void> {
		return this.timezoneProxy.loadServerOffsetFromUTC().map(res => {
			if (res !== this.getServerOffsetFromUTC()) {
				this.serverOffsetFromUTCSubject.next(res);
				this.serverClientOffsetDifferenceSubject.next(this.getLocalOffsetFromUTC() - res);
				this.setLogManagerOffsetDifference();
			}
		});
	}

	/**
	 * Runs the server offset check and force runs the client and server offset check on completion.
	 */
	public checkServerUtcMinutesOffset(): void {
		this.loadServerOffsetFromUTC().pipe(first(), untilDestroyed(this)).subscribe(() => {
			this.checkOffsetDifferenceState();
		});
	}

	/**
	 * Checks if the client and server timezones differ. In case they are different, stored server and client offset difference is updated.
	 */
	public checkOffsetDifferenceState() {
		let currOffsetDifference = this.getLocalOffsetFromUTC() - this.getServerOffsetFromUTC();

		if (currOffsetDifference !== this.getStoredOffsetDifference()) {
			this.serverClientOffsetDifferenceSubject.next(currOffsetDifference);
			this.setLogManagerOffsetDifference();
		}

		this.serverTimeSubject.next(this.getServerTime());
	}

	/**
	 * Stops running server offset check and client wrong time check tasks
	 */
	public clearIntervals() {
		if (this.checkServerUtcMinutesOffsetTimer) {
			clearInterval(this.checkServerUtcMinutesOffsetTimer);
		}

		if (this.checkOffsetDifferenceStateTimer) {
			clearInterval(this.checkOffsetDifferenceStateTimer);
		}
	}
}
